<script setup>
// [PROSPEK] 2026-04-05 Add watch, timeStampAppendedURL for delayed attachment reactivity
import { ref, computed, watch } from 'vue';
// [/PROSPEK]
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import { ATTACHMENT_TYPES } from '../constants';
// [PROSPEK] 2026-04-05 Cache-bust delayed attachment URLs
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
// [/PROSPEK]

const emit = defineEmits(['error']);
const hasError = ref(false);
const showGallery = ref(false);
const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const handleError = () => {
  hasError.value = true;
  emit('error');
};

const attachment = computed(() => {
  return attachments.value[0];
});

// [PROSPEK] 2026-04-05 Cache-busted URL for delayed attachment reactivity
const mediaUrl = computed(() => {
  return attachment.value?.dataUrl
    ? timeStampAppendedURL(attachment.value.dataUrl)
    : '';
});
// [/PROSPEK]

const isReel = computed(() => {
  return attachment.value.fileType === ATTACHMENT_TYPES.IG_REEL;
});

// [PROSPEK] 2026-04-05 Reset video error state when delayed attachment updates arrive
watch(
  attachment,
  () => {
    hasError.value = false;
  },
  { immediate: true }
);
// [/PROSPEK]
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="video"
    @click="showGallery = true"
  >
    <!-- [PROSPEK] 2026-04-05 Error state and cache-busted mediaUrl for delayed attachments -->
    <div
      v-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-center rounded-lg min-h-24 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <!-- [/PROSPEK] -->
    <div v-else class="relative group rounded-lg overflow-hidden">
      <div
        v-if="isReel"
        class="absolute p-2 flex items-start justify-end right-0 pointer-events-none"
      >
        <Icon icon="i-lucide-instagram" class="text-white shadow-lg" />
      </div>
      <video
        controls
        class="rounded-lg skip-context-menu"
        :src="mediaUrl"
        :class="{
          'max-w-48': isReel,
          'max-w-full': !isReel,
        }"
        @click.stop
        @error="handleError"
      />
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
